import {
  BIOMES,
  WORLD_NODES,
  isSlotNodeType,
  type BiomeId,
  type WorldMapNode,
} from '@goodtime/shared';

/**
 * Where things are on the realm map's painting, as fractions (0 to 1) of
 * its width and height, so the art and the hotspots stay aligned whatever
 * the tile set. Nothing tappable sits in the outer 6% (sea and cloud).
 *
 * Live slots stay slots: each painted building that is one of a world's
 * slots opens that slot. A world's other slots are on its board (tap the
 * world's name).
 */

/** A painted building that opens a slot. */
export interface Landmark {
  /** The slot's name, as the slot list has it. */
  name: string;
  biome: BiomeId;
  x: number;
  y: number;
}

export const LANDMARKS: readonly Landmark[] = [
  // Greendale Kingdom
  { name: 'Forest Reels', biome: 'kingdom', x: 0.28, y: 0.4 },
  { name: 'Royal Reels', biome: 'kingdom', x: 0.397, y: 0.259 },
  { name: "Knights' Quest", biome: 'kingdom', x: 0.583, y: 0.527 },
  { name: "Wizard's Tower", biome: 'kingdom', x: 0.537, y: 0.322 },
  // Saltspray Cove
  { name: 'Stormy Seas', biome: 'cove', x: 0.064, y: 0.45 },
  { name: 'Mermaid Cove', biome: 'cove', x: 0.082, y: 0.601 },
  { name: 'Pirate Plunder', biome: 'cove', x: 0.098, y: 0.337 },
  { name: 'Buried Treasure', biome: 'cove', x: 0.189, y: 0.703 },
  // Frostpeak Range
  { name: 'Frost Reels', biome: 'frost', x: 0.485, y: 0.092 },
  { name: 'Ice Cavern', biome: 'frost', x: 0.41, y: 0.146 },
  { name: "Yeti's Den", biome: 'frost', x: 0.563, y: 0.161 },
  { name: 'Glacier Keep', biome: 'frost', x: 0.273, y: 0.117 },
  // Dustwood Frontier
  { name: 'Dustwood Reels', biome: 'frontier', x: 0.337, y: 0.745 },
  { name: 'Gold Mine', biome: 'frontier', x: 0.54, y: 0.699 },
  { name: 'Saloon', biome: 'frontier', x: 0.459, y: 0.801 },
  // Neon Heights
  { name: 'Neon Reels', biome: 'neon', x: 0.918, y: 0.44 },
  { name: 'Arcade', biome: 'neon', x: 0.912, y: 0.625 },
  // Sunscar Sands
  { name: 'Desert Caravan', biome: 'egypt', x: 0.723, y: 0.84 },
  { name: "Pharaoh's Tomb", biome: 'egypt', x: 0.804, y: 0.708 },
  { name: 'Eye of Ra', biome: 'egypt', x: 0.684, y: 0.664 },
  // Wonderfizz Works
  { name: 'Sherbet Square', biome: 'candy', x: 0.71, y: 0.283 },
  { name: 'Lollipop Lane', biome: 'candy', x: 0.846, y: 0.244 },
  { name: 'Cocoa River', biome: 'candy', x: 0.757, y: 0.375 },
  { name: 'Fizz Factory', biome: 'candy', x: 0.742, y: 0.146 },
];

/** Where each world's name is lettered on the painting. */
export const WORLD_LABELS: Readonly<Record<BiomeId, { x: number; y: number }>> = {
  kingdom: { x: 0.47, y: 0.43 },
  cove: { x: 0.15, y: 0.245 },
  frost: { x: 0.345, y: 0.07 },
  candy: { x: 0.8, y: 0.115 },
  neon: { x: 0.885, y: 0.345 },
  egypt: { x: 0.79, y: 0.6 },
  frontier: { x: 0.36, y: 0.665 },
};

/** The slot a landmark opens. */
export function landmarkNode(l: Landmark): WorldMapNode | undefined {
  const biome = BIOMES.findIndex((b) => b.id === l.biome);
  return WORLD_NODES.find((n) => n.biome === biome && n.name === l.name && isSlotNodeType(n.type));
}

/** Every slot in a world, in its list order. */
export function worldSlots(biome: number): WorldMapNode[] {
  return WORLD_NODES.filter((n) => n.biome === biome && isSlotNodeType(n.type));
}

/** The landmark for a slot, when the painting has one. */
export function landmarkFor(nodeId: number): Landmark | undefined {
  return LANDMARKS.find((l) => landmarkNode(l)?.id === nodeId);
}

/** The world (index into BIOMES) a slot belongs to; 0 when unknown. */
export function worldOf(nodeId: number): number {
  return WORLD_NODES.find((n) => n.id === nodeId)?.biome ?? 0;
}

/** A world's middle on the painting: the middle of its landmarks. */
export function worldCentre(biome: number): { x: number; y: number } {
  const id = BIOMES[biome]?.id;
  const ls = LANDMARKS.filter((l) => l.biome === id);
  if (ls.length === 0) return id ? WORLD_LABELS[id] : { x: 0.5, y: 0.5 };
  return {
    x: ls.reduce((a, l) => a + l.x, 0) / ls.length,
    y: ls.reduce((a, l) => a + l.y, 0) / ls.length,
  };
}

/** World indexes (into BIOMES) from west to east, for the world switcher. */
export const WORLDS_WEST_TO_EAST: readonly number[] = BIOMES.map((_, i) => i).sort(
  (a, b) => worldCentre(a).x - worldCentre(b).x,
);

/** The world in view at a point of the painting: the world of the nearest
 *  painted building or world name. */
export function nearestWorld(x: number, y: number): number {
  const points: { biome: BiomeId; x: number; y: number }[] = [
    ...LANDMARKS,
    ...BIOMES.map((b) => ({ biome: b.id, ...WORLD_LABELS[b.id] })),
  ];
  let best: BiomeId = 'kingdom';
  let bestD = Infinity;
  for (const p of points) {
    const d = Math.hypot(p.x - x, (p.y - y) * (2336 / 3504));
    if (d < bestD) {
      bestD = d;
      best = p.biome;
    }
  }
  return Math.max(
    0,
    BIOMES.findIndex((b) => b.id === best),
  );
}
